import { mergeProgress as mergeEarned, type Progress as EarnedProgress, type LevelProgress, type ClearClock } from '@engineer-os/engine';
export { type LevelProgress, type ClearClock } from '@engineer-os/engine';
import type { PlausibilityFlag } from '@engineer-os/engine';
import { forgetMirrors, mirror, mirroredCopies } from './lib/vault';
import { forgetAllKept } from './lib/worksave';
import { getStrings, type Strings } from './lib/strings';
import type { PublicMission } from './types';

/**
 * Player progress, kept in the browser.
 *
 * Stars follow the brief's mastery signals: hint depth is the first one.
 *   3 stars  cleared with no hints that were charged for
 *   2 stars  cleared with up to three of them
 *   1 star   cleared after the solution rung, which is the only one that costs
 *            a second star (`starsFor`)
 * XP is awarded once per level on first clear, plus a bonus for three stars.
 * Replaying can raise stars; the XP difference is added.
 *
 * Where it lives. The record this file reads before the first render is the
 * one in localStorage, and that has not changed. What has changed is that it
 * is no longer the only copy: `saveProgress` also mirrors it into IndexedDB
 * and the Cache Storage API (lib/vault.ts), and `restoreFromMirrors` reads
 * those back after the page is up and merges them in. The merge is what makes
 * that safe — it is a max, an or and an earliest in every field, so a copy
 * recovered from a mirror can only ever add. A learner whose localStorage was
 * cleared on its own, evicted for space, or expired by a browser that does
 * that after a week away, gets their levels back without noticing anything
 * happened.
 *
 * Everything here lives in the learner's own browser, so everything here can
 * be edited by the learner. The design accepts that a cheater cheats only
 * themselves; what this file adds is honesty, not enforcement. A clear the
 * grader could not vouch for (see the engine's plausibility check) is still
 * a clear: it unlocks the next level and earns its XP, but it is recorded as
 * unverified and drawn hollow on the map until a plausible replay verifies
 * it. The stored blob is signed so that a devtools edit shows up the same
 * way. Nothing in this file ever blocks a learner or discards progress.
 */

/** Why a passing grade could not be vouched for, as the engine reports it. */
export type { PlausibilityFlag } from '@engineer-os/engine';

/**
 * What a learner has found hard, by area, across every level they have
 * cleared.
 *
 * Every level carries `mastery_tags` — 257 distinct ones across the course,
 * `shell.processes`, `git.rebase`, `debugging.locate` — and until this
 * existed, exactly one thing in the whole product read them: a disclosure
 * default on the level screen that opens the reflog panel on levels tagged
 * for it. That was the entire memory this product had of what a particular
 * learner struggles with, which is why the mentor greets somebody on their
 * fortieth level in the same words it greeted them on their first.
 *
 * Keyed by the part of a tag before the first dot, so about forty keys rather
 * than 257. A prefix is the grain a learner would recognize being spoken to
 * about — "branches keep catching you out" — and `shell.pipes` and
 * `shell.redirect` are not two separate memories to somebody losing an hour
 * to both. Forty keys also keeps the record small enough to stop being a
 * question (see `Progress.struggle` for the arithmetic).
 *
 * Read through `struggledWith`, never field by field: three callers ask this
 * question (the mentor's `recall` cue, the warm-up's default state, the
 * reflection) and three thresholds would be three different products.
 */
export interface StruggleTally {
  /**
   * Levels carrying this prefix that the learner has cleared, counted once
   * each. The denominator: four charged rungs across twenty levels is a
   * learner doing fine, and across three levels is a learner in trouble.
   */
  levels: number;
  /**
   * Charged hint rungs taken on them. Charged, not opened: `FREE_HINTS` and
   * the rungs the app pushed at a stuck learner are already subtracted before
   * a clear is recorded, so this is help that was gone looking for.
   */
  hints: number;
  /**
   * Checks that failed before the clear. Taken from `LevelProgress.attempts`,
   * which the play screen only advances on a check that did not pass, so this
   * is a count the record was already keeping rather than a second one.
   */
  checks: number;
  /**
   * Named wrong states the mentor recognized on the way (docs/mentor-plan.md
   * §3.2). Zero until the debrief of §3.9 passes the number in; a level with
   * no situations authored contributes nothing here and nothing is inferred
   * from the silence.
   */
  situations: number;
}

/** The whole memory, keyed by mastery-tag prefix. */
export type StruggleRecord = Record<string, StruggleTally>;

/**
 * The record this browser keeps: everything the engine calls progress, plus
 * the one field that is not progress.
 *
 * Declared here rather than in `packages/engine` because `struggle` is not
 * something two devices have to agree about in order for a level to be worth
 * what it is worth. It is not signed, it is not in a backup code, it does not
 * enter `sameProgress`, and it decides nothing about stars or XP. It is what
 * this learner's browser remembers about this learner.
 *
 * **It must survive the account sync, and today it does not.** Three places
 * outside this file drop it, and each one is a small change in a file this
 * change does not own:
 *
 *   - `validateProgress` (worker/account.ts) ends with a closed list of
 *     top-level keys — `version`, `xp`, `levels`, `lastClear` — and answers
 *     `progress.struggle is not a field` to anything else, which is a 400 on
 *     the push. The key has to join that list, with a validator beside the
 *     others: at most ~64 keys matching a short lowercase name, each holding
 *     four counts. The 64 KB ceiling is not the problem — a 200-level record
 *     is about 29.8 KB and forty struggle keys add about 2.3 KB, measured,
 *     not assumed.
 *   - `mergeProgress` in the engine rebuilds the record from four fields, so
 *     a pull would wipe the memory. The wrapper below fixes that for every
 *     caller that imports the merge from here; `lib/progress-sync.ts` imports
 *     it from the engine directly and is the one that still needs telling.
 *   - `useProgress`'s `publish()` copies `{version, xp, levels, lastClear}`
 *     into render state, so the components would read `struggle` as absent
 *     even while storage held it.
 *
 * Optional, not required, for the reason `LevelProgress.skipped` is: a field
 * written into every record would change the stored bytes of every record
 * that exists, and make every signed-in browser push a record that means
 * exactly what the one on the account already meant. It appears on the first
 * clear of a level that has tags, and a learner who has none has no memory,
 * which is the correct answer rather than an empty one.
 *
 * **It never reaches analytics.** `docs/analytics.md` has a "What is never
 * collected" section and this belongs on that side of it: the event vocabulary
 * is closed (lib/events.ts), no event carries a progress record, and nothing
 * here is keyed to a visitor id. What a person finds hard is the most personal
 * thing this product knows about them, and the only two places it is allowed
 * to exist are this browser and that learner's own account row.
 */
export interface Progress extends EarnedProgress {
  struggle?: StruggleRecord;
}

/** What actually sits in localStorage: the progress plus a signature over it. */
interface StoredProgress extends Progress {
  sig: string;
}

/** The v1 blob, kept only long enough to migrate it. */
interface ProgressV1 {
  version: 1;
  xp: number;
  levels: Record<string, Omit<LevelProgress, 'verified' | 'flags'>>;
}

const KEY = 'engineeros.progress.v2';
const KEY_V1 = 'engineeros.progress.v1';
const THREE_STAR_BONUS = 50;

/**
 * Two clears of different levels fewer than this many seconds apart cannot
 * both have been played: nobody reads a brief, loads a level and solves it in
 * ten seconds. This is a physical floor, not a pace judgement; an expert
 * honestly clears level 0 in twenty seconds and must never trip it. Two
 * clears of the *same* level are not measured against it at all: see
 * `ClearClock.missionId`.
 */
const SPRINT_FLOOR_MS = 10_000;

/**
 * The signing key. It ships in the bundle, and that is fine: the signature
 * is there to notice a hand edit in devtools, not to resist someone who
 * reads the source. Anyone who goes that far has cheated only themselves,
 * which the design already accepts.
 */
const SIGNING_KEY = 'engineeros:progress:2026-09';

/**
 * The ladder. Each threshold is placed on a world boundary: a learner who
 * finishes a chapter is promoted for it, and the last rank lands on the last
 * level rather than five levels early, which is why the numbers are not
 * round. The first five are unchanged from the twenty-level course, so no
 * one is demoted by the course growing underneath them.
 *
 *   0      Intern                   level 0
 *   1000   Junior Engineer          level 6
 *   2500   Engineer                 level 12
 *   4000   Senior Engineer          level 16
 *   5500   Service Owner            level 19, the old finale
 *   10000  Tech Lead                level 27, end of The Codebase
 *   16000  Staff Engineer           level 35, end of Production
 *   22000  Principal Engineer       level 42, end of Data
 *   29000  Distinguished Engineer   level 49, the finale
 *
 * Three-star clears pay a bonus on top, so a learner who needs no hints
 * arrives at each rank a level or two early. That is the reward.
 *
 * What is here is the ladder: an id and a threshold, which are the parts a
 * learner never reads. The name on a rung and the line under it are prose
 * and live in the catalogue (strings/progress.ts, `ranks`), keyed by these
 * ids — so that the thresholds cannot drift between two languages and a
 * promotion happens at the same XP in both.
 */
const RANK_LADDER: { id: RankId; xp: number }[] = [
  { id: 'intern', xp: 0 },
  { id: 'junior', xp: 1000 },
  { id: 'engineer', xp: 2500 },
  { id: 'senior', xp: 4000 },
  { id: 'owner', xp: 5500 },
  { id: 'lead', xp: 10000 },
  { id: 'staff', xp: 16000 },
  { id: 'principal', xp: 22000 },
  { id: 'distinguished', xp: 29000 },
];

export type RankId = keyof Strings['progress']['ranks'];

export interface Rank {
  /** Stable across languages. Compare ranks by this, never by `name`. */
  id: RankId;
  name: string;
  xp: number;
  blurb: string;
}

/**
 * The ladder with its words on, in the language the page is in right now.
 *
 * A function rather than a constant, because a constant would be built once
 * at module load and would still be in the language the tab opened in an
 * hour after somebody flipped the switch. `getStrings()` rather than the
 * hook for the reason the whole file is not a component: `rankFor` is called
 * from three different screens and from the clear overlay's own arithmetic.
 */
export function ranks(): Rank[] {
  const words = getStrings().progress.ranks;
  return RANK_LADDER.map(({ id, xp }) => ({ id, xp, name: words[id].name, blurb: words[id].blurb }));
}

/**
 * Rungs of the ladder a learner gets without paying for them.
 *
 * Rung 1 is a question by contract — "which of these three is already true?"
 * (docs/missions.md) — and charging a star for a question was the single
 * worst thing about the old help system: it priced *asking* rather than
 * *being told*, and a learner who is merely unsure does not gamble a star to
 * find out whether they are stuck. They close the tab instead.
 *
 * So the first rung is free, always: what that change was about is what gets
 * counted into `hintsUsed`, not what a hint costs. (What the rungs below it
 * cost is `starsFor`'s business, and it has since moved — see it.) The
 * play screen subtracts this and the rungs the app pushed at a stuck learner
 * (state/useStuck.ts) before it records a clear, and the panel that states
 * the price and the panel that awards the stars both read the same figure —
 * there is no third place for the two to disagree.
 *
 * A number rather than a literal `1` at three call sites, because the two
 * sentences that quote the policy to a learner are generated from it.
 */
export const FREE_HINTS = 1;

/**
 * What a clear is worth, given how many rungs were actually charged for.
 *
 * The ladder is five rungs (docs/missions.md): a question, where to look,
 * which command, the concept, and last the answer. `FREE_HINTS` takes the
 * first one off the bill, so the rungs this function sees are 0 for a learner
 * who took only the free question and 4 for one who read the solution.
 *
 * The threshold used to be `<= 2`, and that priced the bottom of the ladder
 * at nothing. Charged rungs 3 and 4 — the concept, and then the answer —
 * both landed on one star, so a learner who had opened the third rung was
 * being told, correctly, that the explanation and the full solution were now
 * free. The one moment the ladder exists to make somebody hesitate was the
 * one moment it cost them nothing, and the rung it waved them past is the
 * rung that ends the thinking.
 *
 * So the last step is the one that costs. Everything up to and including the
 * concept is two stars, and reading the answer is the only thing that drops a
 * clear to one:
 *
 *   rungs paid   0      1       2       3           4
 *   what it is   —      look    command concept     the answer
 *   stars        3      2       2       2           1
 *
 * That leaves the incentive where it belongs. A learner who is stuck can walk
 * three rungs down towards understanding at a flat price and still be at the
 * same standing they reached at the first one — the ladder is not a slope
 * they are sliding down — and the single place the price moves again is the
 * step that replaces the work with the answer. Stopping at the concept is now
 * strictly better than taking the solution, which is the whole point and was
 * not true before.
 *
 * Still 1|2|3, not 0|1|2|3. Zero stars is what a *skipped* level carries
 * (`recordSkip`), and a clear that read the solution is not a skip: the
 * learner did the work with help and the map should not draw the two the
 * same. One star is the floor because one star means "you were shown the
 * answer" — which is now exactly what it means, and is what the clear panel
 * has always said it means (strings/progress.ts, `noteOneStar`).
 *
 * `HintLadder` quotes this price before a rung is opened by calling this
 * function; it must not restate the curve. It did, and the two drifted.
 */
export function starsFor(hintsUsed: number): 1 | 2 | 3 {
  if (hintsUsed === 0) return 3;
  if (hintsUsed <= 3) return 2;
  return 1;
}

export function xpFor(base: number, stars: number): number {
  return base + (stars === 3 ? THREE_STAR_BONUS : 0);
}

/* ── The struggle record ─────────────────────────────────────────────────── */

/**
 * Failed checks on one level that are simply the loop the product is made of.
 *
 * The same number `useStuck` uses to decide that three checks with the same
 * failing set is a learner who has run out of things to try: up to three is
 * working, and only what is past it is friction worth remembering. Counting
 * every failed check would make the busiest prefix win on every learner,
 * because the levels with the most tags are also the longest ones.
 */
const ROUTINE_CHECKS = 3;

/**
 * How many cleared levels a prefix needs before it is allowed to speak.
 *
 * Two, and this is the load-bearing half of the threshold. One hard level is
 * a hard level, not a pattern, and a memory that fires the moment anybody has
 * one bad hour would put a "this caught you out before" cue on the level
 * immediately after it for every learner in the course — which is the noise
 * failure, and noise is how a feature like this gets switched off. Memory
 * across levels should only speak when the same *area* has been hard more
 * than once.
 */
const STRUGGLE_MIN_LEVELS = 2;

/**
 * The score at which a prefix counts as struggled with.
 *
 * Four, against `struggleScore` below, where a charged rung counts one, a
 * recognized wrong state counts one, and failed checks count only past three
 * per level. So it takes four rungs the learner went looking for, or two
 * rungs and two levels that each ran five checks over, or any mixture — and
 * it takes them across at least two levels.
 *
 * Why not lower: with `FREE_HINTS` taking the first rung off the bill, one
 * charged rung is an ordinary level for an ordinary learner, and a cue that
 * fires on one of those fires on everybody, everywhere, which is the same as
 * not firing at all. Why not higher: a prefix like `shell` carries dozens of
 * levels, and a bar that only a catastrophe clears would leave the mentor
 * saying nothing to precisely the learner it was built for. Four is the point
 * where a single bad level no longer explains the number.
 */
const STRUGGLE_THRESHOLD = 4;

/**
 * A ceiling on the keys kept, so a hand-edited or corrupt record cannot grow
 * the thing the account has to accept. The course has about forty prefixes;
 * this is generous room above that and still nothing against the 64 KB the
 * Worker allows.
 */
const STRUGGLE_MAX_KEYS = 64;

/** A prefix as it may be written down: short, lowercase, no dots left in it. */
const STRUGGLE_KEY = /^[a-z][a-z0-9_-]{0,31}$/;

/**
 * The prefixes a level's mastery tags name, deduplicated and sorted.
 *
 * Deduplicated because a level tagged `shell.pipes` and `shell.redirect` is
 * one level about the shell, not two; counting it twice would make the
 * `levels` denominator lie in exactly the direction that suppresses the
 * signal. Sorted so that two devices that cleared the same levels in
 * different orders write the same bytes.
 *
 * It takes a list that may not be there, and that is not defensiveness for
 * its own sake: the paid half of the catalogue is built out of locked stubs,
 * and a stub carries a title and a level number and very little else. The
 * last thing that walked the client catalogue assuming every field was
 * present threw on a stub and took the whole locale switch down with it. An
 * absent list is a level with no tags, which is a level this memory has
 * nothing to say about.
 */
export function tagPrefixes(tags: readonly string[] | undefined): string[] {
  if (!Array.isArray(tags)) return [];
  const out = new Set<string>();
  for (const tag of tags) {
    if (typeof tag !== 'string') continue;
    const prefix = tag.trim().toLowerCase().split('.')[0] ?? '';
    if (STRUGGLE_KEY.test(prefix)) out.add(prefix);
  }
  return [...out].sort();
}

/**
 * How much trouble one tally describes.
 *
 * Hints and recognized wrong states are worth one each because both are the
 * learner saying, in the only two ways the product lets them, that the level
 * was not going anywhere. Checks are worth nothing until they are past
 * `ROUTINE_CHECKS` per level, because failing a check is the loop, not the
 * trouble.
 */
function struggleScore(t: StruggleTally): number {
  return t.hints + t.situations + Math.max(0, t.checks - ROUTINE_CHECKS * t.levels);
}

/**
 * The area of this level a learner has the most trouble with, or null when
 * none of them has earned the right to be mentioned.
 *
 * One question, asked one way, by all three callers: the mentor leading with
 * a `recall` cue, the warm-up deciding whether to open by default, and the
 * reflection naming the earlier level. Each of them writing its own threshold
 * would be three products disagreeing about whether this learner is struggling
 * — on the same screen.
 *
 * Ties go to the first prefix alphabetically, which is arbitrary and is meant
 * to be: what matters is that the same state always produces the same answer,
 * so that the warm-up and the mentor on one level are talking about the same
 * thing.
 */
export function struggledWith(p: Progress, tags: readonly string[] | undefined): string | null {
  const struggle = p.struggle;
  if (!struggle) return null;
  let best: string | null = null;
  let bestScore = 0;
  for (const prefix of tagPrefixes(tags)) {
    const tally = struggle[prefix];
    if (!tally || tally.levels < STRUGGLE_MIN_LEVELS) continue;
    const score = struggleScore(tally);
    if (score >= STRUGGLE_THRESHOLD && score > bestScore) {
      best = prefix;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Two memories into one, and the same one whichever way round they are given.
 *
 * A max per counter, not a sum. The merge runs again on every pull, against a
 * record it has already merged, so a sum would climb for ever on a learner
 * with two devices and turn a comfortable pass into a struggle nobody had.
 * The max is also the honest reading: each device counted the clears it
 * watched, and the one that watched more is the one to believe.
 *
 * Idempotent and commutative, for the reason the engine's own merge is: two
 * devices each merge and push in their own order, and an answer that depended
 * on which side was first would have them pushing at one another for ever.
 */
function mergeStruggle(a: StruggleRecord | undefined, b: StruggleRecord | undefined): StruggleRecord | undefined {
  if (!a) return b && sortStruggle(b);
  if (!b) return sortStruggle(a);
  const out: StruggleRecord = {};
  for (const key of [...new Set([...Object.keys(a), ...Object.keys(b)])].sort()) {
    const x = a[key];
    const y = b[key];
    out[key] = !x || !y
      ? { ...(x ?? y)! }
      : {
          levels: Math.max(x.levels, y.levels),
          hints: Math.max(x.hints, y.hints),
          checks: Math.max(x.checks, y.checks),
          situations: Math.max(x.situations, y.situations),
        };
  }
  return out;
}

/** The same record with its keys in a fixed order, so equal memories are equal bytes. */
function sortStruggle(s: StruggleRecord): StruggleRecord {
  const out: StruggleRecord = {};
  for (const key of Object.keys(s).sort()) out[key] = { ...s[key]! };
  return out;
}

/**
 * A memory read back from somewhere that is not this code: localStorage, a
 * mirror, the account.
 *
 * Nothing here is trusted and nothing here is repaired into something
 * plausible. A key that is not a prefix, a counter that is not a count, more
 * keys than the course has — any of those and that entry is dropped. **A
 * wiped or damaged record has to degrade to "no memory", never to wrong
 * memory**: a mentor that opens with "branches keep catching you out" at
 * somebody who has never touched a branch is worse than a mentor that says
 * nothing, because it is confidently wrong about the learner rather than
 * merely silent.
 */
function readStruggle(value: unknown): StruggleRecord | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const out: StruggleRecord = {};
  let kept = 0;
  for (const key of Object.keys(value as Record<string, unknown>).sort()) {
    if (kept >= STRUGGLE_MAX_KEYS || !STRUGGLE_KEY.test(key)) continue;
    const t = (value as Record<string, unknown>)[key];
    if (typeof t !== 'object' || t === null) continue;
    const { levels, hints, checks, situations } = t as Record<string, unknown>;
    if (![levels, hints, checks, situations].every((n) => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0)) continue;
    out[key] = { levels: levels as number, hints: hints as number, checks: checks as number, situations: situations as number };
    kept += 1;
  }
  return kept ? out : undefined;
}

/* ── Signature ───────────────────────────────────────────────────────────── */

/**
 * A keyed 64-bit digest (cyrb53-style mixing, two lanes). It is synchronous,
 * which crypto.subtle is not, and loadProgress has to run before the first
 * render. The key is folded in at both ends so that neither prefix nor
 * suffix extensions of the content keep the digest.
 */
function digest(text: string): string {
  // NUL as the separator: it cannot appear in JSON, so key and content never blur.
  const SEP = String.fromCharCode(0);
  const input = `${SIGNING_KEY}${SEP}${text}${SEP}${SIGNING_KEY}`;
  let h1 = 0xdeadbeef ^ input.length;
  let h2 = 0x41c6ce57 ^ input.length;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

/**
 * The content the signature covers: everything but the signature, in a fixed
 * field order.
 *
 * `struggle` is deliberately not in it, and the omission is the policy rather
 * than an oversight. The signature exists to notice an edit that changes what
 * a level is worth, and the answer to noticing one is `markTampered`, which
 * hollows out every star on the map. A learner who hand-edits their own
 * memory of which topics were hard has cheated themselves out of a mentor cue
 * and nothing else; taking their stars for it would be wildly out of
 * proportion. It also keeps `sameProgress` meaning "the same earned record",
 * so a memory that moved on its own never costs a Worker write — and it never
 * has to, because the only thing that writes it is a first clear, which moves
 * `levels` in the same breath.
 */
function canonical(p: Progress): string {
  return JSON.stringify({ version: p.version, xp: p.xp, levels: p.levels, lastClear: p.lastClear });
}

/**
 * The engine's merge, plus the field the engine does not know about.
 *
 * The engine rebuilds the record from its four fields, so without this a
 * single pull from the account would wipe the memory of every level the
 * learner has ever cleared. Wrapped here rather than fixed there because the
 * engine's `Progress` is the shape the Worker validates, and until
 * `validateProgress` accepts the key there is nothing for the engine to merge
 * (see `Progress` above). Every caller that imports the merge from this
 * module is covered; `lib/progress-sync.ts` imports it from the engine and is
 * the one still to be told.
 */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const merged: Progress = mergeEarned(a, b);
  const struggle = mergeStruggle(a.struggle, b.struggle);
  if (struggle) merged.struggle = struggle;
  return merged;
}

/**
 * The signature over a record. Exported because a backup code carries the
 * same record and is checked the same way (lib/backup.ts); a second signing
 * scheme for the same bytes would be a second thing to keep in step.
 */
export function signProgress(p: Progress): string {
  return digest(canonical(p));
}

/* ── Load / save ─────────────────────────────────────────────────────────── */

function fresh(): Progress {
  return { version: 2, xp: 0, levels: {}, lastClear: null };
}

/**
 * Old clears predate verification and there is no evidence either way, so
 * they are treated as verified. Punishing existing learners for a feature
 * that did not exist when they played would be a false positive by design.
 */
function migrateV1(v1: ProgressV1): Progress {
  const levels: Record<string, LevelProgress> = {};
  for (const [id, lp] of Object.entries(v1.levels)) {
    levels[id] = { ...lp, verified: lp.completed };
  }
  return { version: 2, xp: v1.xp, levels, lastClear: null };
}

/**
 * A signature mismatch means the blob was changed outside this file. Nothing
 * is wiped: XP and unlocks stand, and every cleared level is simply marked
 * unverified with a flag saying why. A plausible replay clears it again.
 *
 * Exported for the one other place bytes claiming to be a record arrive from
 * outside: a pasted backup code (lib/backup.ts). `detail` says which of the
 * two it was, in the same register as the engine's own details — a lowercase
 * fragment with no full stop, because Play joins them into one sentence after
 * a colon. Both halves of it are prose a learner reads, so the default comes
 * from the catalogue and the caller's own reason had better come from there
 * too.
 */
export function markTampered(p: Progress, detail = getStrings().progress.flags.tampered): void {
  for (const lp of Object.values(p.levels)) {
    if (!lp.completed) continue;
    lp.verified = false;
    const flags = lp.flags ?? [];
    if (!flags.some((f) => f.code === 'progress-tampered')) flags.push({ code: 'progress-tampered', detail });
    lp.flags = flags;
  }
}

/**
 * One stored blob — from localStorage or from a mirror, which hold the same
 * string — as a record, or null if it is not one. A signature mismatch is not
 * a refusal: the record is returned with its clears marked, exactly as before.
 */
function parseStored(raw: string | null): Progress | null {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as Partial<StoredProgress>;
    if (!stored || stored.version !== 2 || !stored.levels) return null;
    const p: Progress = {
      version: 2,
      xp: typeof stored.xp === 'number' ? stored.xp : 0,
      levels: stored.levels,
      lastClear: stored.lastClear ?? null,
    };
    // Checked rather than taken, because these bytes came from outside this
    // code and a memory that is not a memory has to read as no memory at all.
    // Assigned before the signature is checked and deliberately not covered
    // by it: see `canonical`.
    const struggle = readStruggle(stored.struggle);
    if (struggle) p.struggle = struggle;
    if (stored.sig !== signProgress(p)) markTampered(p);
    return p;
  } catch {
    return null;
  }
}

/**
 * Which course this browser's record was earned on. Bumping it starts every
 * browser over once: the record, its mirrors, the kept level work and the
 * practice reviews. Accounts are reset on the server by advancing the
 * progress epoch, which progress-sync already follows by replacing; this is
 * the half the server cannot reach, a learner who never signed in.
 *
 * 2026-10-03: the agent-first course (levels 0-19 redesigned) replaced the
 * old one and everyone started fresh.
 * 2026-10-28: levels are solved by the learner's own connected agent
 * (lib/agent-first.ts); everyone started fresh again.
 */
const COURSE_KEY = 'engineeros.course';
const COURSE = '2026-10-28';

/** Settles once a course reset's tombstones are down; restoreFromMirrors waits for it. */
let courseReset: Promise<void> = Promise.resolve();

function startNewCourse(): void {
  try {
    if (localStorage.getItem(COURSE_KEY) === COURSE) return;
    // A browser that has never been here has nothing to forget, and a reset
    // that wrote tombstones for it would only be noise.
    const visited = localStorage.getItem(KEY) !== null || localStorage.getItem(KEY_V1) !== null;
    localStorage.removeItem(KEY);
    localStorage.removeItem(KEY_V1);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('engineeros.learning-practice.')) localStorage.removeItem(key);
    }
    localStorage.setItem(COURSE_KEY, COURSE);
    // Overwritten rather than deleted, for the reason forgetMirrors gives:
    // a mirror left behind would merge the old course straight back in.
    if (visited) courseReset = Promise.all([forgetMirrors(KEY), forgetAllKept()]).then(() => undefined);
  } catch {
    /* storage unavailable: there is nothing here to reset */
  }
}

export function loadProgress(): Progress {
  startNewCourse();
  try {
    const p = parseStored(localStorage.getItem(KEY));
    if (p) return p;
    const rawV1 = localStorage.getItem(KEY_V1);
    if (rawV1) {
      const v1 = JSON.parse(rawV1) as ProgressV1;
      if (v1 && v1.version === 1 && v1.levels) {
        const p = migrateV1(v1);
        saveProgress(p);
        return p;
      }
    }
  } catch {
    /* storage unavailable or corrupt: start fresh */
  }
  return fresh();
}

export function saveProgress(p: Progress): void {
  let json: string;
  try {
    const stored: StoredProgress = { ...p, sig: signProgress(p) };
    json = JSON.stringify(stored);
  } catch {
    return;
  }
  try {
    localStorage.setItem(KEY, json);
    // Written by this build, so earned on this course.
    localStorage.setItem(COURSE_KEY, COURSE);
  } catch {
    // Storage is full, disabled, or partitioned away. The mirrors below are
    // written anyway: one of them may well be the store that still works, and
    // this is exactly the browser where the only copy is worth having.
  }
  mirror(KEY, json);
}

/**
 * Whatever the mirrors kept, as one record, or null when they kept nothing.
 *
 * Every surviving copy is merged rather than the newest one taken, because
 * "newest" is a wall clock the learner's own machine sets, and because the
 * merge makes the question moot: two copies of one learner's progress merge
 * to the better of each field whichever order they arrive in. The caller
 * merges the result into what it already has, so this can only ever add.
 *
 * Async, and deliberately not on the path to the first render. The page still
 * comes up from localStorage; this lands a moment later and the map updates
 * if there was anything to add.
 */
export async function restoreFromMirrors(): Promise<Progress | null> {
  await courseReset;
  const copies = await mirroredCopies(KEY);
  let out: Progress | null = null;
  for (const raw of copies) {
    const p = parseStored(raw);
    if (p) out = out ? mergeProgress(out, p) : p;
  }
  return out;
}

export function levelProgress(p: Progress, missionId: string): LevelProgress {
  return (
    p.levels[missionId] ?? {
      completed: false,
      stars: 0,
      hintsUsed: 0,
      attempts: 0,
      completedAt: null,
      xpEarned: 0,
      verified: false,
    }
  );
}

/* ── Recording ───────────────────────────────────────────────────────────── */

function now(missionId: string): ClearClock {
  return { wall: Date.now(), mono: performance.now(), origin: performance.timeOrigin, missionId };
}

/**
 * How long ago the previous clear of a *different* level was, or null when
 * there is nothing to measure against: no previous clear, a previous clear
 * of this same level (a reset-and-replay, which is what a flagged learner
 * is told to do), or a record too old to say which level it was, which is
 * read as "this one" because guessing the other way could only produce a
 * false flag.
 *
 * Within one page load the monotonic clock is used, because Date.now() can
 * be dragged around by the system clock. After a reload performance.now()
 * restarts from zero, so the wall clock is the only comparison left; it is
 * the fallback, and a learner who sets their clock backwards to make two
 * clears look far apart has, again, fooled only their own map.
 */
function sinceLastClear(p: Progress, at: ClearClock): number | null {
  const last = p.lastClear;
  if (!last) return null;
  if (!last.missionId || last.missionId === at.missionId) return null;
  if (last.origin === at.origin) return at.mono - last.mono;
  return at.wall - last.wall;
}

export interface ClearRecord {
  stars: 1 | 2 | 3;
  xpGained: number;
  firstClear: boolean;
  improved: boolean;
  /** False when the grade carried plausibility flags or the clear followed another too closely. */
  verified: boolean;
  flags: PlausibilityFlag[];
  /**
   * The total before and after this clear, so the standing can be shown
   * advancing rather than simply arrived. Equal when nothing was gained.
   */
  xpBefore: number;
  xpAfter: number;
}

/**
 * Record a clear. Returns what changed so the UI can celebrate the right
 * numbers.
 *
 * `flags` are the plausibility flags the grader attached to the passing
 * grade. A flagged clear still counts in every way that matters for play;
 * it is only recorded as unverified. Verification moves in one direction: a
 * level already verified stays verified whatever a replay looks like, since
 * a returning learner replays levels they know very quickly, and a level
 * cleared unverified becomes verified by a clean replay.
 */
/**
 * What the level screen saw that the record does not already hold.
 *
 * One field so far, and optional, so that the play screen keeps compiling
 * while the debrief (docs/mentor-plan.md §3.9) is still being built. Every
 * other struggle counter is derived from something `recordClear` already had
 * in its hands, which is the point: this is a memory *of* the record, not a
 * second record to be kept in step with it.
 */
export interface ClearEffort {
  /** Distinct named wrong states the mentor recognized on this level. */
  situations?: number;
}

export function recordClear(
  p: Progress,
  mission: PublicMission,
  hintsUsed: number,
  flags: PlausibilityFlag[] = [],
  effort: ClearEffort = {},
): ClearRecord {
  const prev = levelProgress(p, mission.id);
  const stars = starsFor(hintsUsed);
  const earned = xpFor(mission.xp, stars);
  const firstClear = !prev.completed;
  const improved = !firstClear && stars > prev.stars;
  const xpGained = Math.max(0, earned - prev.xpEarned);
  const xpBefore = p.xp;
  const at = now(mission.id);

  let verified = prev.verified;
  let nextFlags: PlausibilityFlag[] = [];
  if (!prev.verified) {
    nextFlags = [...flags];
    const gap = sinceLastClear(p, at);
    if (gap !== null && gap >= 0 && gap < SPRINT_FLOOR_MS) {
      // The figure is a duration in a sentence, so it goes through the
      // catalogue rather than through `toFixed`: one decimal place, and the
      // digits and the separator the reader's language formats them with.
      nextFlags.push({ code: 'level-sprint', detail: getStrings().progress.flags.levelSprint(gap / 1000) });
    }
    verified = nextFlags.length === 0;
  }

  const next: LevelProgress = {
    completed: true,
    stars: Math.max(stars, prev.stars) as 0 | 1 | 2 | 3,
    hintsUsed: firstClear || improved ? hintsUsed : prev.hintsUsed,
    attempts: prev.attempts + 1,
    completedAt: prev.completedAt ?? at.wall,
    xpEarned: Math.max(earned, prev.xpEarned),
    verified,
  };
  if (!verified) next.flags = nextFlags;
  // A level that was skipped and is now cleared keeps the mark, for the same
  // reason the merge carries it: dropping it here would make this browser's
  // record and the account's disagree about a level both of them have seen
  // the whole history of. Nothing reads it once `completed` is true.
  if (prev.skipped) next.skipped = true;
  p.levels[mission.id] = next;
  p.xp += xpGained;
  p.lastClear = at;
  // The memory is written from the clear that finished the level, and only
  // from that one. `firstClear` is the distinction the function already draws
  // and the only one that makes this derived rather than authoritative: a
  // replay would add a second set of counts for one level, and an improvement
  // would record a learner getting *better* at a topic as more evidence that
  // they are bad at it. Both readings are worse than no memory, and no memory
  // is what a learner who wipes their record correctly gets.
  if (firstClear) noteStruggle(p, mission, hintsUsed, prev.attempts, effort.situations ?? 0);
  saveProgress(p);
  return { stars, xpGained, firstClear, improved, verified, flags: verified ? [] : nextFlags, xpBefore, xpAfter: p.xp };
}

/**
 * One cleared level, folded into the memory.
 *
 * `hintsUsed` is what the clear was priced at, so it is already net of the
 * free first rung and of every rung the app pushed at a stuck learner — help
 * that was gone looking for, which is the thing worth remembering.
 * `failedChecks` is `LevelProgress.attempts` as it stood before this clear,
 * and the play screen only advances that on a check that did not pass, so it
 * is a count of failures and not of effort.
 *
 * Nothing here touches `starsFor`, `xpFor` or `FREE_HINTS`. What a level is
 * worth is a contract the clear overlay and the hint panel both quote, and a
 * memory that changed the price of a level would be a memory a learner would
 * be right to want deleted.
 */
function noteStruggle(p: Progress, mission: PublicMission, hintsUsed: number, failedChecks: number, situations: number): void {
  const prefixes = tagPrefixes(mission.mastery_tags);
  if (prefixes.length === 0) return;
  const struggle: StruggleRecord = { ...(p.struggle ?? {}) };
  for (const prefix of prefixes) {
    const t = struggle[prefix] ?? { levels: 0, hints: 0, checks: 0, situations: 0 };
    struggle[prefix] = {
      levels: t.levels + 1,
      hints: t.hints + Math.max(0, hintsUsed),
      checks: t.checks + Math.max(0, failedChecks),
      situations: t.situations + Math.max(0, situations),
    };
  }
  p.struggle = sortStruggle(struggle);
}

export function recordAttempt(p: Progress, missionId: string): void {
  const prev = levelProgress(p, missionId);
  p.levels[missionId] = { ...prev, attempts: prev.attempts + 1 };
  saveProgress(p);
}

/**
 * The learner moved on without clearing this level.
 *
 * Nothing is earned and nothing is taken: no stars, no XP, `completed` stays
 * false, and the level can be played again at any time. What a skip buys is
 * the *next* level, and the reason to sell it is that the alternative a
 * genuinely stuck learner reaches for is not "try harder" — it is the tab's
 * close button. One level should cost them one level.
 *
 * It is recorded rather than inferred from the level after it, because the
 * map has something to say about it ("come back to this one") that a hole in
 * the history cannot say. A clear afterwards leaves the mark in place and
 * sets `completed` beside it; every reader treats `completed` as the stronger
 * of the two, so the row then draws as cleared.
 */
export function recordSkip(p: Progress, missionId: string): void {
  const prev = levelProgress(p, missionId);
  if (prev.completed || prev.skipped) return;
  p.levels[missionId] = { ...prev, skipped: true };
  saveProgress(p);
}

/* ── Comparison ──────────────────────────────────────────────────────────── */

/** Same content, field for field. What decides whether a merge is worth pushing. */
export function sameProgress(a: Progress, b: Progress): boolean {
  return canonical(a) === canonical(b);
}

/**
 * Open every level, for authoring and review. Development only: the query
 * flag is ignored in a production build and never written, so a learner
 * who finds it in the source gets nothing from it. The localStorage key is
 * still honoured in dev so the flag survives a reload without the query.
 */
export function unlockAll(): boolean {
  // Read through a try, because this is the one line in the module that only
  // a bundler can answer: Vite substitutes `import.meta.env.DEV` for a literal,
  // and a plain runtime has no `import.meta.env` to read a property from. It
  // threw there, which is why the gate below had no test for the whole of the
  // course's life. Outside a bundle the honest answer is "not development".
  let dev = false;
  try {
    dev = import.meta.env.DEV;
  } catch {
    dev = false;
  }
  if (!dev) return false;
  try {
    if (new URLSearchParams(location.search).get('unlock') === 'all') localStorage.setItem('engineeros.unlockAll', '1');
    return localStorage.getItem('engineeros.unlockAll') === '1';
  } catch {
    return false;
  }
}

/**
 * A level is playable when it requires nothing, is already cleared, or every
 * mission it requires is behind the learner — cleared, or deliberately
 * skipped.
 *
 * A skip counts here and nowhere else: it opens the door and earns nothing.
 * That asymmetry is the whole design. Requiring a clear made the course a
 * chain in which one hard level is the end of it, and the learner who cannot
 * pass level 2 does not go and read about git — they leave.
 *
 * This used to look up `level - 1`, which said two things at once: what the
 * prerequisite is, and that there is exactly one of it. Neither survives a
 * course that branches (docs/curriculum.md), so the mission now names its own
 * prerequisites and this only reads them.
 *
 * **It fails closed.** The old lookup ended `prev ? … : true` — a level whose
 * predecessor was missing from the catalogue opened for everybody, and the
 * paid half is built out of locked stubs rather than omissions largely to
 * keep that from ever happening. An unresolvable prerequisite is now a reason
 * to refuse rather than a reason to allow, so a catalogue that loses a level
 * closes a door instead of opening one.
 */
export function isUnlocked(p: Progress, mission: PublicMission, missions: PublicMission[]): boolean {
  if (unlockAll()) return true;
  if (levelProgress(p, mission.id).completed) return true;
  // An entry point. Stated by the mission rather than inferred from its
  // position, so that "this starts a path" cannot be confused with "the level
  // before it did not load". A mission with no `requires` at all never gets
  // this far — engine/runtime.ts refuses the catalogue — which is why an
  // empty list can be read as the deliberate thing it is.
  if (mission.requires.length === 0) return true;
  return mission.requires.every((id) => {
    const prerequisite = missions.find((m) => m.id === id);
    if (!prerequisite) return false;
    return isBehind(levelProgress(p, prerequisite.id));
  });
}

/** Cleared or skipped: the two ways a level stops standing in the way. */
export function isBehind(lp: LevelProgress): boolean {
  return lp.completed || lp.skipped === true;
}

export function rankFor(xp: number): { current: Rank; next: Rank | null; pct: number } {
  const RANKS = ranks();
  let current = RANKS[0]!;
  let next: Rank | null = null;
  for (let i = 0; i < RANKS.length; i++) {
    if (xp >= RANKS[i]!.xp) {
      current = RANKS[i]!;
      next = RANKS[i + 1] ?? null;
    }
  }
  const pct = next ? Math.min(100, Math.round(((xp - current.xp) / (next.xp - current.xp)) * 100)) : 100;
  return { current, next, pct };
}

export function totals(p: Progress, missions: PublicMission[]): { cleared: number; stars: number; maxStars: number; unverified: number } {
  let cleared = 0;
  let stars = 0;
  let unverified = 0;
  for (const m of missions) {
    const lp = levelProgress(p, m.id);
    if (lp.completed) cleared++;
    if (lp.completed && !lp.verified) unverified++;
    stars += lp.stars;
  }
  return { cleared, stars, maxStars: missions.length * 3, unverified };
}

export function starString(n: number): string {
  return [1, 2, 3].map((i) => `<span class="${i <= n ? 'on' : ''}">★</span>`).join('');
}
